import React from 'react'
import styled from 'styled-components'
import useDeltaToHtmlConverter from 'hooks/useDeltaToHtmlConverter'
import { QuillDelta, hasQuillDelta } from './quizDelta'

interface QuizDeltaHtmlProps {
  delta: QuillDelta
  className?: string
}

const QuizDeltaHtml = ({ delta, className }: QuizDeltaHtmlProps): JSX.Element => {
  const html = useDeltaToHtmlConverter(delta)

  return (
    <QuizDeltaHtmlRoot
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

interface QuizDeltaContentProps {
  delta?: QuillDelta | null
  fallbackText?: string
  className?: string
}

// Prefer delta so bold, italics, and images from the API render first.
// Fall back to plain text when the delta key is missing or empty.
export const QuizDeltaContent = ({
  delta,
  fallbackText = '',
  className
}: QuizDeltaContentProps): JSX.Element | null => {
  if (hasQuillDelta(delta)) {
    return <QuizDeltaHtml delta={delta as QuillDelta} className={className} />
  }

  if (!fallbackText) {
    return null
  }

  return <QuizDeltaPlain className={className}>{fallbackText}</QuizDeltaPlain>
}

export default QuizDeltaContent

const quizDeltaSharedStyles = `
  width: 100%;
  min-width: 0;
  color: inherit;
  font: inherit;
  line-height: inherit;
  text-align: left;
  word-break: break-word;
  overflow-wrap: anywhere;

  p {
    margin: 0;
    color: inherit;
    font: inherit;
  }

  strong,
  b {
    font-weight: 700;
    color: inherit;
  }

  em,
  i {
    font-style: italic;
    color: inherit;
  }

  u {
    text-decoration: underline;
    color: inherit;
  }

  img {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 8px 0;
    border-radius: 4px;
  }
`

const QuizDeltaHtmlRoot = styled.div`
  ${quizDeltaSharedStyles}
`

const QuizDeltaPlain = styled.span`
  ${quizDeltaSharedStyles}
  display: inline;
`
